import React, { useEffect, useState } from "react";
import { Bell, Save, X } from "lucide-react";
import Button from "../../components/common/Button";
import Typography from "../../components/common/Typography";
import {
  getReminderSettings,
  ReminderSetting,
  updateReminderSetting,
} from "../../services/organizationService";
import { useToast } from "../../components/CustomToast";

interface CompletionRemindersProps {
  organisationId: string;
  readOnly: boolean;
}

type DraftSetting = ReminderSetting & {
  overdueFromDraft: string;
  repeatEveryValueDraft: number;
  repeatEveryUnitDraft: "days" | "weeks" | "months";
};

const toDraft = (setting: ReminderSetting): DraftSetting => ({
  ...setting,
  overdueFromDraft: setting.overdueFrom || "",
  repeatEveryValueDraft: setting.repeatEveryValue || 7,
  repeatEveryUnitDraft: setting.repeatEveryUnit || "days",
});

const CompletionReminders: React.FC<CompletionRemindersProps> = ({
  organisationId,
  readOnly,
}) => {
  const toast = useToast();
  const [settings, setSettings] = useState<DraftSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getReminderSettings(organisationId);
      setSettings(data.map(toDraft));
    } catch (error) {
      console.error("Unable to load completion reminders:", error);
      toast.showToast("Unable to load completion reminders.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // `organisationId` is the lifecycle key; toast identity is stable in the app.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId]);

  const updateDraft = (
    masterCourseId: string,
    changes: Partial<DraftSetting>,
  ) => {
    setSettings((current) =>
      current.map((setting) =>
        setting.masterCourseId === masterCourseId
          ? { ...setting, ...changes }
          : setting,
      ),
    );
  };

  const save = async (setting: DraftSetting) => {
    if (!setting.overdueFromDraft) {
      toast.showToast("Choose an overdue date or use Clear.", "warning");
      return;
    }
    setSavingId(setting.masterCourseId);
    try {
      await updateReminderSetting(organisationId, {
        masterCourseId: setting.masterCourseId,
        overdueFrom: setting.overdueFromDraft,
        repeatEveryValue: setting.repeatEveryValueDraft,
        repeatEveryUnit: setting.repeatEveryUnitDraft,
      });
      toast.showToast("Completion reminder saved.", "success");
      await load();
    } catch (error) {
      console.error("Unable to save completion reminder:", error);
      toast.showToast("Unable to save completion reminder.", "error");
    } finally {
      setSavingId(null);
    }
  };

  const clear = async (setting: DraftSetting) => {
    setSavingId(setting.masterCourseId);
    try {
      await updateReminderSetting(organisationId, {
        masterCourseId: setting.masterCourseId,
        overdueFrom: null,
      });
      toast.showToast(
        "Completion reminders cleared for this course.",
        "success",
      );
      await load();
    } catch (error) {
      console.error("Unable to clear completion reminder:", error);
      toast.showToast("Unable to clear completion reminder.", "error");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <section
      className="mt-6 rounded-lg border border-gray-200 bg-white shadow-sm"
      aria-labelledby="completion-reminders-heading"
    >
      <div className="border-b border-gray-200 bg-gray-50 p-4">
        <div className="flex items-start gap-3">
          <Bell
            className="mt-0.5 text-green-600"
            size={20}
            aria-hidden="true"
          />
          <div>
            <Typography
              variant="h5"
              weight="semibold"
              className="!text-gray-900"
              id="completion-reminders-heading"
            >
              Completion reminders
            </Typography>
            <Typography variant="small" className="!text-gray-600">
              Set a date and cadence for staff who have not completed each
              assigned course. Leave a course clear to send no reminders.
            </Typography>
          </div>
        </div>
      </div>
      <div className="divide-y divide-gray-100">
        {loading ? (
          <Typography variant="p" className="p-4 !text-gray-600">
            Loading completion reminders…
          </Typography>
        ) : settings.length === 0 ? (
          <Typography variant="p" className="p-4 !text-gray-600">
            No assigned courses are available for reminders.
          </Typography>
        ) : (
          settings.map((setting) => {
            const saving = savingId === setting.masterCourseId;
            return (
              <div
                key={setting.masterCourseId}
                className="grid gap-3 p-4 md:grid-cols-[minmax(180px,1fr)_150px_110px_130px_auto] md:items-end"
              >
                <div>
                  <Typography
                    variant="p"
                    weight="medium"
                    className="!text-gray-900"
                  >
                    {setting.masterCourseTitle}
                  </Typography>
                  {readOnly && (
                    <Typography variant="small" className="!text-amber-700">
                      Read-only access
                    </Typography>
                  )}
                </div>
                <label className="flex flex-col gap-1 text-sm text-gray-700">
                  Overdue from
                  <input
                    aria-label={`Overdue from for ${setting.masterCourseTitle}`}
                    type="date"
                    value={setting.overdueFromDraft}
                    disabled={readOnly || saving}
                    onChange={(event) =>
                      updateDraft(setting.masterCourseId, {
                        overdueFromDraft: event.target.value,
                      })
                    }
                    className="rounded border border-gray-300 px-2 py-1.5 disabled:bg-gray-100"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-gray-700">
                  Repeat every
                  <input
                    aria-label={`Repeat interval for ${setting.masterCourseTitle}`}
                    type="number"
                    min="1"
                    value={setting.repeatEveryValueDraft}
                    disabled={readOnly || saving}
                    onChange={(event) =>
                      updateDraft(setting.masterCourseId, {
                        repeatEveryValueDraft: Math.max(
                          1,
                          Number(event.target.value) || 1,
                        ),
                      })
                    }
                    className="rounded border border-gray-300 px-2 py-1.5 disabled:bg-gray-100"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm text-gray-700">
                  Unit
                  <select
                    aria-label={`Repeat unit for ${setting.masterCourseTitle}`}
                    value={setting.repeatEveryUnitDraft}
                    disabled={readOnly || saving}
                    onChange={(event) =>
                      updateDraft(setting.masterCourseId, {
                        repeatEveryUnitDraft: event.target
                          .value as DraftSetting["repeatEveryUnitDraft"],
                      })
                    }
                    className="rounded border border-gray-300 px-2 py-1.5 disabled:bg-gray-100"
                  >
                    <option value="days">days</option>
                    <option value="weeks">weeks</option>
                    <option value="months">months</option>
                  </select>
                </label>
                {!readOnly && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => clear(setting)}
                      disabled={saving || !setting.overdueFromDraft}
                      startIcon={<X size={16} />}
                      label="Clear"
                    />
                    <Button
                      variant="filled"
                      onClick={() => save(setting)}
                      disabled={saving}
                      startIcon={<Save size={16} />}
                      label={saving ? "Saving…" : "Save"}
                    />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </section>
  );
};

export default CompletionReminders;
